import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs';

import { NON_DIGITS_PATTERN } from '@shared-constants/pattern-constants';
import { EquipmentCostCenterEnum } from '@shared-enums/equipment.enum';
import { IHttpResponseEquipment } from '@shared-interfaces/api';
import { IHttpErrorResponseObject } from '@shared-interfaces/error';

import { ISapRequestFilterQueryProps } from '../../models/api.model';
import { Equipment } from '../../scripts/equipment';
import { ApiService } from '../../services/api.service';
import { DatahubService } from '../../services/datahub.service';
import { MessageService } from '../../services/message.service';

export type ISortableEquipmentKey = keyof Pick<IHttpResponseEquipment, 'Description' | 'CostCenter' | 'EquipmentId'>;

@Component({
  selector: 'equipment-edit',
  templateUrl: './equipment-edit.component.html',
  standalone: false,
})
export class EquipmentEditComponent implements OnInit {
  public filter: FormControl<string> = this.datahub.fb.control('');
  public shop: Array<IHttpResponseEquipment> = [];
  public crew = this.datahub.timesheet.currentEquipmentCrew;
  public readonly equipmentSearch = new FormControl();
  public errorMessage = '';
  public inputText = '';
  public isSearchingEquipment = true;
  public sameCostCenter: IHttpResponseEquipment[] = [];
  public differentCostCenter: IHttpResponseEquipment[] = [];
  public unavailableEquipment: IHttpResponseEquipment[] = [];
  public readonly costCenterControl: FormControl<string> = this.datahub.costCenter.getCostCenterFormControl();
  public activeSearchCostCenter = this.datahub.extDocument.Costcenter1;
  public timesheetCompanyCode = this.datahub.contract.value.CompanyCode;
  public isMoreThan3Chars = false;
  public hasNoPriceInvalidEquipment = false;
  private readonly searchSubject$ = new Subject<string>();

  constructor(
    private readonly destroyRef: DestroyRef,
    public readonly datahub: DatahubService, //this must remain public for now because it is directly used in the HTML template for some reason!
    private readonly messageService: MessageService,
    private readonly apiService: ApiService,
  ) {}

  public ngOnInit(): void {
    this.datahub.costCenter.shouldMissingCostCenterThrowError = false;
    this.searchSubject$.pipe(debounceTime(750), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isMoreThan3Chars = this.inputText.length < 3 && this.inputText.length !== 0;
      this.search();
    });

    if (!this.datahub.timesheet.isTimesheetInProgress) {
      this.datahub.resetApplicationState();
      return;
    }
    this.search();
  }

  public onSearch(): void {
    this.resetSearch();
    this.searchSubject$.next(this.inputText);
  }

  public search(): void {
    const inputBeforeSearch = this.inputText;
    let inputAfterSearch: string;
    if (!this.inputText && !this.costCenterControl.value) {
      this.isSearchingEquipment = false;
      this.errorMessage = 'Please enter a description or cost center to continue';
    } else if (this.inputText) {
      if (this.inputText.length > 2) {
        const queryParams = this.parseSearchValuesForQuery();
        this.apiService.getEquipmentByPartialDescription$(queryParams).subscribe({
          next: (equipment: IHttpResponseEquipment[]) => {
            this.shop = equipment;
            if (this.shop.length === 0) {
              this.isSearchingEquipment = false;
              this.errorMessage = `No Matches Found. \n\nPlease double check the search, or contact your supervisor for help.`;
            } else {
              inputAfterSearch = this.inputText;
              if (inputBeforeSearch === inputAfterSearch) {
                this.filterShop();
              } else {
                this.isSearchingEquipment = true;
                this.onSearch();
              }
            }
          },
          error: (err: IHttpErrorResponseObject) => {
            this.errorMessage =
              'An error occurred while fetching equipment. \n\nIf this issue persists please contact your supervisor for help';
            console.error('An error occurred while fetching equipment', err);
          },
        });
      } else if (this.inputText.length > 0) {
        this.isSearchingEquipment = false;
      }
    } else {
      const queryParams = {
        CostCenter: this.datahub.costCenter.getCostCenterValue(),
        ValidTo: this.datahub.date.getFilterString(),
      };
      this.apiService.getEquipment$(queryParams).subscribe({
        next: (equipments: IHttpResponseEquipment[]) => {
          // temporary fix, when item transfers mid week, we need to re evaluate it by equipment number to get REAL invalid dates.
          const updatedEquipment$: Observable<IHttpResponseEquipment | undefined>[] = [];
          equipments.forEach((equipment) => {
            if (equipment.CCWeek.split(',').length < 7) {
              const singleEqpParams = {
                EquipmentId: equipment.EquipmentId,
                ValidTo: this.datahub.date.getFilterString(),
              };
              updatedEquipment$.push(this.datahub.apiService.oldGetOneEquipment$(singleEqpParams));
            }
          });
          if (updatedEquipment$.length > 0) {
            combineLatest(updatedEquipment$).subscribe((newEquipment) => {
              newEquipment.forEach((eqp) => {
                const equipmentToChange = equipments.find((eq) => eq.EquipmentId === eqp?.EquipmentId);
                if (equipmentToChange) equipmentToChange.InvalidDates = eqp?.InvalidDates ?? equipmentToChange.InvalidDates;
              });
              this.shop = equipments;
              if (this.shop.length === 0) {
                this.isSearchingEquipment = false;
                this.errorMessage = `No Matches Found. \n\nPlease double check the search, or contact your supervisor for help.`;
              } else {
                inputAfterSearch = this.inputText;
                if (inputBeforeSearch === inputAfterSearch) {
                  this.filterShop();
                } else {
                  this.isSearchingEquipment = true;
                  this.onSearch();
                }
              }
            });
          } else {
            this.shop = equipments;
            if (this.shop.length === 0) {
              this.isSearchingEquipment = false;
              this.errorMessage = `No Matches Found. \n\nPlease double check the search, or contact your supervisor for help.`;
            } else {
              inputAfterSearch = this.inputText;
              if (inputBeforeSearch === inputAfterSearch) {
                this.filterShop();
              } else {
                this.isSearchingEquipment = true;
                this.onSearch();
              }
            }
          }
        },
        error: (err) => {
          this.errorMessage =
            'An error occurred while fetching equipment. \n\nIf this issue persists please contact your supervisor for help';
          console.error('An error occurred while fetching equipment', err);
        },
      });
    }
  }

  private parseSearchValuesForQuery(): ISapRequestFilterQueryProps {
    if (this.inputText && NON_DIGITS_PATTERN.test(this.inputText)) {
      const equipmentDescriptionSearch = this.inputText;
      const eqpNameSearchQuery = {
        ValidTo: this.datahub.date.getFilterString(),
        Description: `*${equipmentDescriptionSearch}*`,
      };
      return eqpNameSearchQuery;
    } else {
      const eqpIdSearchQuery = {
        ValidTo: this.datahub.date.getFilterString(),
        EquipmentId: this.inputText,
      };
      return eqpIdSearchQuery;
    }
  }

  public back(): void {
    this.datahub.timesheet.navigateToSelf();
  }

  public add(eqp: IHttpResponseEquipment | null, arrayName = 'sameCostCenter'): void {
    if (!eqp) return;
    if (this.crew.some((c) => c.EquipmentId === eqp.EquipmentId)) {
      this.messageService.info(`#${eqp.EquipmentId}-${eqp.Description}, has already been added`);
      return;
    }
    switch (arrayName) {
      case 'differentCostCenter':
        this.differentCostCenter = this.differentCostCenter.filter((x) => x !== eqp);
        break;
      case 'sameCostCenter':
        this.sameCostCenter = this.sameCostCenter.filter((x) => x !== eqp);
        break;
      default:
        console.error('arrayName does not exist');
    }
    this.datahub.timesheet.addEquipment(eqp);
  }

  public remove(eqp: Equipment): void {
    eqp.removeSelf(this.crew);
    this.shop.push(eqp.getSapObject());
    this.returnToProperArray(eqp);
  }

  private returnToProperArray(eqp: Equipment | IHttpResponseEquipment): void {
    this.activeSearchCostCenter = this.costCenterControl.value
      ? this.costCenterControl.value
      : this.datahub.extDocument.Costcenter1;
    if (eqp.InvalidDates.includes(this.datahub.date.getDayOfWeek().toString())) {
      this.unavailableEquipment.push(eqp);
      this.unavailableEquipment = this.sortByCCandEquipmentId(this.unavailableEquipment);
    } else if (eqp.CostCenter === this.activeSearchCostCenter) {
      this.sameCostCenter.push(eqp);
      this.sameCostCenter = this.sortByCCandEquipmentId(this.sameCostCenter);
    } else if (eqp.CostCenter !== this.activeSearchCostCenter) {
      this.differentCostCenter.push(eqp);
      this.differentCostCenter = this.sortByCCandEquipmentId(this.differentCostCenter);
    }
  }

  public clearAll(): void {
    this.errorMessage = '';
    this.datahub.timesheet.currentEquipmentCrew.forEach((equipment) => {
      this.returnToProperArray(equipment);
    });
    this.datahub.timesheet.clearAllEquipments();
  }

  private filterShop(): void {
    this.errorMessage = '';
    this.datahub.timesheet.currentEquipmentCrew.forEach((equipment: Equipment) => {
      equipment.removeSelf(this.shop);
    });
    this.shop = this.sortByCCandEquipmentId(this.shop);
    this.separateShop();
    this.isSearchingEquipment = false;
  }

  private separateShop(): void {
    this.activeSearchCostCenter = this.costCenterControl.value
      ? this.costCenterControl.value
      : this.datahub.extDocument.Costcenter1;
    this.shop.map((equipment) => {
      if (equipment.InvalidDates.includes(this.datahub.date.getDayOfWeek().toString())) {
        this.unavailableEquipment.push(equipment);
      } else if (equipment.CostCenter === this.activeSearchCostCenter) {
        this.sameCostCenter.push(equipment);
      } else {
        this.differentCostCenter.push(equipment);
      }
    });
  }

  public launchUnavailableEquipmentModal(equipmentCostCenter: string) {
    let bodyText: string;
    switch (equipmentCostCenter) {
      case EquipmentCostCenterEnum.MarkedForSale:
        bodyText = 'This equipment is marked as under construction. Please contact your supervisor for help.';
        break;
      case EquipmentCostCenterEnum.UnderConstruction:
        bodyText = 'This equipment is marked for sale. Please contact your supervisor for help.';
        break;
      default:
        bodyText = 'The piece of equipment selected is missing costs for this year. Please contact your supervisor for help.';
    }
    this.datahub
      .launchResponsiveModal('simple-display', {
        titleText: 'You cannot add this equipment to your timesheet.',
        bodyText,
      })
      .afterClosed()
      .subscribe({
        error: (err) => {
          console.error('Error when closing the "unavailable equipment" modal', err);
        },
      });
  }

  private sortByCCandEquipmentId(arrayToBeSorted: IHttpResponseEquipment[]): IHttpResponseEquipment[] {
    return arrayToBeSorted.sort(
      (x, y) => Number(x.CostCenter) - Number(y.CostCenter) || x.EquipmentId.localeCompare(y.EquipmentId),
    );
  }

  private resetSearch(): void {
    this.shop = [];
    this.sameCostCenter = [];
    this.differentCostCenter = [];
    this.unavailableEquipment = [];
    this.isSearchingEquipment = true;
    this.errorMessage = '';
  }
}
